import React from "react";
import { useFetchMasterAccountDashboardLoginsQuery } from "../services/clientauth";
import { useGetMasterAccountPreferenceSummaryQuery } from "../services/clientdetails";
import { DefReactSelectOption } from "../types/DefReactSelectOption";
import { PagedResults } from "../types/PagedResults";
import { MasterAccountPreferencesType } from "../types/MasterAccountPreferencesTypes";
import { MasterAccountSummaryContactDetailsType } from "../types/MasterAccountContactDetailTypes";
import { MasterAccountDashboardLoginType } from "../types/MasterAccountDashboardLoginType";

const ClientPreferencesSummaryContext = React.createContext<{
    dashboardLogins: MasterAccountDashboardLoginType[],
    emailAddressOptions: DefReactSelectOption<MasterAccountSummaryContactDetailsType>[]
    errors: any[],
    hasEmails: boolean,
    isAllCommunicationsStopped: boolean,
    isDashboardNotificationsEnabled: boolean,
    isEmailNotificationsEnabled: boolean,
    isError: boolean,
    isLoading: boolean,
    masterAccountId: string,
    preferences: MasterAccountPreferencesType | null
}>({ 
    dashboardLogins: [],
    emailAddressOptions: [],
    errors: [],
    hasEmails: false,
    isAllCommunicationsStopped: false,
    isDashboardNotificationsEnabled: false,
    isEmailNotificationsEnabled: false,
    isError: false,
    isLoading: false,
    masterAccountId: '',
    preferences: null
});

export const useClientPreferencesSummary = () => React.useContext(ClientPreferencesSummaryContext);

export const ClientPreferencesSummaryProvider = ({ 
    children, 
    masterAccountId 
} : Readonly<{
    children: React.ReactNode,
    masterAccountId: string
}>) => {
    const { data: authData, error: authError, isLoading: isAuthLoading } = useFetchMasterAccountDashboardLoginsQuery<PagedResults<MasterAccountDashboardLoginType>>({ masterAccountId, page: null, limit: null }, { skip: !masterAccountId });
    const { data: prefData, error: prefError, isLoading: isPrefLoading } = useGetMasterAccountPreferenceSummaryQuery<MasterAccountPreferencesType>({ masterAccountId }, { skip: !masterAccountId });
    const isError = authError || prefError;
    const isLoading = isAuthLoading || isPrefLoading;

    const dashboardLogins : MasterAccountDashboardLoginType[] = React.useMemo(() => {
        if (!authData || authData.results.length <= 0) {
            return [];
        }
        return authData.results as MasterAccountDashboardLoginType[];
    }, [ authData ]);

    const emailAddressOptions : DefReactSelectOption<MasterAccountSummaryContactDetailsType>[] = React.useMemo(() => {
        if (!prefData || !prefData.emails || prefData.emails.length < 1) {
            return [];
        }
        return prefData.emails.map((el: MasterAccountSummaryContactDetailsType) => ({  
            label: el.details,
            value: el.details,
            isDisabled: false,
            isFixed: false,
            data: el
        } as DefReactSelectOption<MasterAccountSummaryContactDetailsType>));
    }, [ prefData ]);

    const errors : any[] = React.useMemo(() => {
        let errs : any[] = [];
        if (authError) {
            errs.push(authError);
        }
        if (prefError) {
            errs.push(prefError);
        }
        return errs;
    }, [ authError, prefError ]);
    
    return (
        <ClientPreferencesSummaryContext.Provider value={{
            isError,
            isLoading,
            dashboardLogins,
            errors,
            emailAddressOptions,
            hasEmails: prefData?.emails && prefData?.emails.length > 0,
            isAllCommunicationsStopped: prefData?.communication?.isAllCommunicationsStopped ?? false,
            isDashboardNotificationsEnabled: prefData?.communication?.isDashboardEnabled ?? false,
            isEmailNotificationsEnabled: prefData?.communication?.isEmailNotificationsEnabled ?? false,
            masterAccountId,
            preferences: prefData
        }}>
            {children}
        </ClientPreferencesSummaryContext.Provider>
    );
};